/**
 * KB-3319 — the one place staff and camp directors see who changed a field and put an old value back.
 * Replaces the location Version-history tab; also mounts in the org tabs and the offer dialog.
 *
 * Failure mode this component is built AGAINST: for almost every entity the contribution log is empty
 * (the KB-3292 backfill seeded `winner.cid: "legacy"` and wrote no rows). A failed call, a refused call,
 * and a real empty history would all render as one blank list unless kept distinct. So the three empty
 * states below never collapse into one, and an error is never rendered as "no history".
 *
 * The callable is the authority on `audience`: a director's rows arrive allowlist-redacted regardless of
 * the `audience` prop, so interactive controls gate on `result.audience`, never on the prop alone.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { doc, getDoc } from "firebase/firestore";

import { toast } from "@/components/ui/sonner";
import { db } from "@/services/firebaseService";
import { useAuth } from "@/context/AuthContext";
import { describeActor } from "@/services/campDataReviewInbox";
import { FieldStateProvider, useFieldState, fetchFieldContribution } from "@/services/campDataFieldState";
import FieldVendorHint from "@/components/common/FieldVendorHint";
import FieldOwnershipBadge from "@/components/common/FieldOwnershipBadge";
import { updateOffer } from "@/services/offer";
import { updateCampLocation } from "@/services/campLocationUpdateService";
import {
  updateCampOrganization,
  updateCampOrganizationInTransaction,
  type CampOrganization,
} from "@/services/campOrganizationService";
import {
  getEntityHistory,
  type EntityHistoryResult,
  type EntityKind,
  type HistoryAudience,
  type HistoryBatch,
  type HistoryItem,
} from "@/services/entityHistoryService";
import {
  buildRestorePatch,
  isOrgContactsRestore,
  restoreCollectionFor,
  restoreOrgContacts,
} from "@/components/common/entityHistoryRestore";

interface EntityHistoryPanelProps {
  kind: EntityKind;
  id: string;
  audience: HistoryAudience;
}

function clampValue(v: unknown): string {
  if (v == null) return "(empty)";
  let s: string;
  if (typeof v === "string") s = v;
  else if (typeof v === "number" || typeof v === "boolean") s = String(v);
  else {
    try {
      s = JSON.stringify(v);
    } catch {
      return "(complex value)";
    }
  }
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}
function fullValue(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
}
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : format(d, "MMM d, yyyy h:mm a");
}
function actorClassOf(b: HistoryBatch): string {
  return b.sourceClass ?? b.actorLabel ?? "unknown";
}
function actorTextOf(b: HistoryBatch, audience: HistoryAudience): string {
  return audience === "staff" ? describeActor(b.sourceClass, b.actorId, b.resolvedName) : b.actorLabel ?? "";
}

/** The three-part Compare-to-vendor line + the reused KB-3306 controls. Staff only; needs a FieldStateProvider. */
const CompareToVendor: React.FC<{ fieldPath: string; catalogValue: unknown; onUseVendorValue: (v: unknown) => void }> = ({
  fieldPath,
  catalogValue,
  onUseVendorValue,
}) => {
  const { record } = useFieldState(fieldPath);
  const [vendorValue, setVendorValue] = useState<unknown>(undefined);
  const [vendorLoaded, setVendorLoaded] = useState(false);

  const cid = record?.latestPipeline?.cid;
  useEffect(() => {
    if (!cid) {
      setVendorLoaded(false);
      setVendorValue(undefined);
      return;
    }
    let mounted = true;
    fetchFieldContribution(cid)
      .then((row) => {
        if (!mounted) return;
        setVendorValue(row ? row.value : undefined);
        setVendorLoaded(!!row);
      })
      .catch(() => mounted && setVendorLoaded(false));
    return () => {
      mounted = false;
    };
  }, [cid]);

  return (
    <div className="mt-1 pl-2 border-l-2 border-muted" data-testid="ehp-compare" data-field-path={fieldPath}>
      {!record?.latestPipeline || !vendorLoaded ? (
        // Never imply the values match when there is no vendor value to compare against.
        <p className="text-sm text-muted-foreground" data-testid="ehp-compare-novendor">
          No vendor value recorded
        </p>
      ) : (
        <p className="text-sm" data-testid="ehp-compare-line">
          Catalog <span className="font-medium">{clampValue(catalogValue)}</span> · Vendor{" "}
          <span className="font-medium">{clampValue(vendorValue)}</span> · {record.policy}
        </p>
      )}
      <FieldVendorHint fieldPath={fieldPath} audience="staff" onUseVendorValue={onUseVendorValue} />
      <FieldOwnershipBadge fieldPath={fieldPath} audience="staff" />
    </div>
  );
};

export const EntityHistoryPanel: React.FC<EntityHistoryPanelProps> = ({ kind, id, audience }) => {
  const { currentUser } = useAuth();
  const [result, setResult] = useState<EntityHistoryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fieldFilter, setFieldFilter] = useState<string>("all");
  const [actorFilter, setActorFilter] = useState<string>("all");
  const [compareOpen, setCompareOpen] = useState<Set<string>>(new Set());
  const [pendingRestore, setPendingRestore] = useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getEntityHistory({ kind, id });
      setResult(res);
    } catch {
      setError(true);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [kind, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const isStaff = result?.audience === "staff";

  const fieldOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const b of result?.batches ?? []) for (const it of b.items) if (!seen.has(it.fieldKey)) seen.set(it.fieldKey, it.fieldPath);
    return Array.from(seen, ([key, path]) => ({ key, path }));
  }, [result]);

  const actorOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const b of result?.batches ?? []) seen.add(actorClassOf(b));
    return Array.from(seen);
  }, [result]);

  const filteredBatches = useMemo(() => {
    if (!result) return [];
    const out: HistoryBatch[] = [];
    for (const b of result.batches) {
      if (actorFilter !== "all" && actorClassOf(b) !== actorFilter) continue;
      const items = fieldFilter === "all" ? b.items : b.items.filter((it) => it.fieldKey === fieldFilter);
      if (items.length === 0) continue;
      out.push({ ...b, items });
    }
    return out;
  }, [result, fieldFilter, actorFilter]);

  const toggleCompare = useCallback((fieldPath: string) => {
    setCompareOpen((prev) => {
      const next = new Set(prev);
      if (next.has(fieldPath)) next.delete(fieldPath);
      else next.add(fieldPath);
      return next;
    });
  }, []);

  const writeField = useCallback(
    async (fieldPath: string, value: unknown) => {
      const uid = currentUser?.uid ?? "";
      if (isOrgContactsRestore(kind, fieldPath)) {
        // Read and write in one transaction, keeping contacts[0]'s CURRENT do-not-contact flag.
        await updateCampOrganizationInTransaction(
          db,
          id,
          (current) => ({ contacts: restoreOrgContacts(fieldPath, value, current) as CampOrganization["contacts"] }),
          uid,
        );
        return;
      }
      // Nested/array paths need the current doc reconstructed; a plain scalar does not.
      const needsDoc = fieldPath.split("/").filter(Boolean).length > 1;
      let currentDoc: Record<string, unknown> | null = null;
      if (needsDoc) {
        const snap = await getDoc(doc(db, restoreCollectionFor(kind), id));
        currentDoc = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
      }
      const patch = buildRestorePatch(fieldPath, value, currentDoc);
      if (kind === "offer") await updateOffer(db, id, patch, uid);
      else if (kind === "campLocation") await updateCampLocation(db, id, patch, uid);
      else await updateCampOrganization(db, id, patch, uid);
    },
    [currentUser, kind, id],
  );

  const confirmRestore = useCallback(
    async (item: HistoryItem) => {
      setRestoreBusy(true);
      try {
        await writeField(item.fieldPath, item.value);
        toast.success("Restored — the change is recorded in the history");
        setPendingRestore(null);
        await load();
      } catch {
        toast.error("Could not restore this value");
      } finally {
        setRestoreBusy(false);
      }
    },
    [writeField, load],
  );

  if (loading) {
    return (
      <div data-testid="entity-history-panel">
        <p className="text-sm text-muted-foreground">Loading history…</p>
      </div>
    );
  }

  if (error || !result) {
    // (a) The call failed or was denied — an error with Retry, NEVER a blank "no history".
    return (
      <div data-testid="entity-history-panel">
        <div className="text-sm text-destructive" data-testid="ehp-error" role="alert">
          Couldn&apos;t load history.{" "}
          <button type="button" className="underline underline-offset-2" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filterActive = fieldFilter !== "all" || actorFilter !== "all";

  const renderEmpty = () => {
    // (c) THIS page shows nothing but more rows exist — a browser-side filter hid them, or (for a
    // director) every row on the page was an omitted class while later pages remain. Never say "nothing
    // recorded" while `nextCursor` advertises more.
    if ((result.batches.length > 0 && filterActive) || result.nextCursor) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="ehp-empty-filtered">
          No matching rows on this page.
        </p>
      );
    }
    if (result.legacyWinners) {
      // (b) No rows, and the state doc shows only pre-tracking winners.
      return (
        <p className="text-sm text-muted-foreground" data-testid="ehp-empty-legacy">
          Nothing recorded since field tracking began.
        </p>
      );
    }
    return (
      <p className="text-sm text-muted-foreground" data-testid="ehp-empty">
        No history recorded yet.
      </p>
    );
  };

  const body = (
    <div data-testid="entity-history-panel" data-audience={result.audience}>
      <div className="flex gap-2 mb-3 flex-wrap">
        <select
          className="border rounded px-2 py-1 text-sm"
          data-testid="ehp-field-filter"
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
        >
          <option value="all">All fields</option>
          {fieldOptions.map((f) => (
            <option key={f.key} value={f.key}>
              {f.path}
            </option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-1 text-sm"
          data-testid="ehp-actor-filter"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
        >
          <option value="all">All contributors</option>
          {actorOptions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      {filteredBatches.length === 0 ? (
        renderEmpty()
      ) : (
        <ul className="space-y-3" data-testid="ehp-rows">
          {filteredBatches.map((b, bi) => (
            <li key={`${b.when}-${bi}`} className="border rounded p-2" data-testid="ehp-batch">
              <div className="text-sm font-medium">
                {actorTextOf(b, result.audience)} · <span className="text-muted-foreground">{fmtWhen(b.when)}</span>
              </div>
              <ul className="mt-1 space-y-2">
                {b.items.map((it) => (
                  <li key={it.cid} data-testid="ehp-item" data-field-key={it.fieldKey}>
                    <div className="flex items-baseline gap-2 flex-wrap text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{it.fieldPath}</span>
                      <span className="truncate max-w-[24rem]" title={fullValue(it.value)} data-testid="ehp-value">
                        {it.hasValueRef ? "(large value)" : clampValue(it.value)}
                      </span>
                      <span className="text-xs rounded bg-muted px-1.5 py-0.5" data-testid="ehp-outcome">
                        {it.outcomeLabel}
                      </span>
                      {isStaff && it.evidence?.pageUrl && (
                        <a
                          href={it.evidence.pageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs underline underline-offset-2"
                          data-testid="ehp-evidence"
                        >
                          evidence
                        </a>
                      )}
                      {isStaff && it.restorable && pendingRestore !== it.cid && (
                        <button
                          type="button"
                          className="text-xs underline underline-offset-2"
                          data-testid="ehp-restore"
                          onClick={() => setPendingRestore(it.cid)}
                        >
                          Restore this value
                        </button>
                      )}
                      {isStaff && (
                        <button
                          type="button"
                          className="text-xs underline underline-offset-2"
                          data-testid="ehp-compare-toggle"
                          onClick={() => toggleCompare(it.fieldPath)}
                        >
                          Compare to vendor
                        </button>
                      )}
                    </div>

                    {isStaff && it.restorable && pendingRestore === it.cid && (
                      <div className="mt-1 text-sm" data-testid="ehp-restore-confirm">
                        Restore this value?{" "}
                        <button
                          type="button"
                          className="underline underline-offset-2 disabled:opacity-50"
                          data-testid="ehp-restore-confirm-yes"
                          disabled={restoreBusy}
                          onClick={() => void confirmRestore(it)}
                        >
                          Yes, restore
                        </button>{" "}
                        <button
                          type="button"
                          className="underline underline-offset-2"
                          data-testid="ehp-restore-cancel"
                          onClick={() => setPendingRestore(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {isStaff && compareOpen.has(it.fieldPath) && (
                      <CompareToVendor
                        fieldPath={it.fieldPath}
                        catalogValue={it.value}
                        onUseVendorValue={(v) => void writeField(it.fieldPath, v).then(() => load())}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {result.nextCursor && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="ehp-more">
          More history is available on an earlier page.
        </p>
      )}

      {result.snapshots && (
        <details className="mt-4" data-testid="ehp-snapshots">
          <summary className="text-sm font-medium cursor-pointer">
            Snapshot history until {result.snapshots[0] ? fmtWhen(result.snapshots[0].timestamp) : "—"}
          </summary>
          <ul className="mt-2 space-y-1">
            {result.snapshots.map((s) => (
              <li key={s.id} className="text-sm text-muted-foreground" data-testid="ehp-snapshot">
                Version {s.versionNumber ?? "?"} · {fmtWhen(s.timestamp)}
                {isStaff && s.changeDescription ? ` · ${s.changeDescription}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );

  // Compare-to-vendor reads field state through the KB-3306 provider; only staff open it.
  return isStaff ? (
    <FieldStateProvider kind={kind} id={id} enabled>
      {body}
    </FieldStateProvider>
  ) : (
    body
  );
};

export default EntityHistoryPanel;
